/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印提取逻辑处理 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-12 18:02:24
 */

import { useCallback } from 'react'
import { useAITaskEvent } from './useAITaskEvent'
import { useUserId } from '../../../../app/hooks'
import { message } from 'antd'
import {
  WatermarkExtractionStatus,
  PhotoItem,
  WatermarkItem
} from '../context/WatermarkBatchContext'
import {
  WatermarkApplyOutputResult,
  WatermarkApplyStatus
} from './useWatermarkApply'
import { fetchOutputFromUrl } from './useAITaskEvent'

// 定义水印提取结果类型
interface WatermarkExtractionResult {
  successPhotos: PhotoItem[]
  failedPhotos: PhotoItem[]
  timeoutPhotos: PhotoItem[] // 提取超时的照片
  watermarkData: {
    watermarkIDMap: Map<string, string> // key: photo URL, value: watermarkID
    watermarkDataMap: Map<string, WatermarkItem[]> // key: photo URL, value: watermark data
  }
}

// 定义水印提取 Hook 返回类型
export interface UseWatermarkExtractionReturn {
  // 执行水印提取
  extractWatermarks: (
    pendingPhotos: PhotoItem[],
    onPhotosStatusUpdate: (
      photos: PhotoItem[],
      status: WatermarkExtractionStatus
    ) => void,
    onWatermarkDataUpdate: (
      photoUrl: string,
      watermarkID: string,
      watermarkData: WatermarkItem[]
    ) => void,
    onExtractionComplete?: (successPhotos: PhotoItem[]) => void,
    onExtractionStart?: (photoCount: number) => void,
    onExtractionEnd?: () => void
  ) => Promise<void>
}

/**
 * 水印提取逻辑处理 Hook
 * 负责处理水印提取的完整流程，包括创建任务、轮询结果、处理返回数据
 */
export const useWatermarkExtraction = (): UseWatermarkExtractionReturn => {
  // 使用 AI 任务 hook
  const { createAITask, pollAITaskResult } = useAITaskEvent()

  // 获取用户ID
  const userId = useUserId()

  /**
   * 规范化 URL，去除查询参数，只保留协议、主机和路径
   */
  const normalizeUrl = (url: string): string => {
    if (!url) return ''
    try {
      const urlObj = new URL(url)
      return `${urlObj.protocol}//${urlObj.host}${urlObj.pathname}`
    } catch {
      // 如果 URL 解析失败，返回原始字符串（去除查询参数）
      return url.split('?')[0]
    }
  }

  /**
   * 处理水印提取结果
   * @param outputData 接口返回的数据
   * @param pendingPhotos 待提取的照片列表
   * @returns 处理结果
   */
  const processExtractionResults = useCallback(
    (
      outputData: any[],
      pendingPhotos: PhotoItem[]
    ): WatermarkExtractionResult => {
      const successPhotos: PhotoItem[] = []
      const failedPhotos: PhotoItem[] = []
      const timeoutPhotos: PhotoItem[] = [] // 提取超时的照片
      const watermarkData: {
        watermarkIDMap: Map<string, string> // key: photo URL, value: watermarkID
        watermarkDataMap: Map<string, WatermarkItem[]> // key: photo URL, value: watermark data
      } = {
        watermarkIDMap: new Map<string, string>(),
        watermarkDataMap: new Map<string, WatermarkItem[]>()
      }

      outputData.forEach((item, index) => {
        // 使用src字段匹配照片，而不是seq字段
        // 注意：接口返回的src字段可能包含反引号和空格，需要去除
        const srcUrl = item.src && item.src.replace(/`/g, '').trim()
        const normalizedSrcUrl = normalizeUrl(srcUrl || '')

        // 只使用 smallURL 进行匹配（规范化后的 URL 和原始 URL 都支持）
        const photoIndex = pendingPhotos.findIndex((pendingPhoto) => {
          const normalizedSmallURL = normalizeUrl(pendingPhoto.smallURL || '')
          return (
            normalizedSmallURL === normalizedSrcUrl ||
            // 也支持原始 URL 匹配（向后兼容）
            pendingPhoto.smallURL === srcUrl
          )
        })

        const extractionStatus = Number(item.success)

        if (photoIndex !== -1) {
          const photo = pendingPhotos[photoIndex]

          // 根据 success 值判断状态
          let watermarkStatus: WatermarkExtractionStatus
          if (extractionStatus === WatermarkApplyStatus.EDITED) {
            watermarkStatus = WatermarkExtractionStatus.COMPLETED
          } else if (extractionStatus === 3) {
            // success === 3 表示提取超时
            watermarkStatus = WatermarkExtractionStatus.EXTRACTING_TIMEOUT
          } else {
            watermarkStatus = WatermarkExtractionStatus.FAILED
          }

          // 创建新的照片对象，保留 taskID 和所有其他字段
          const updatedPhoto = {
            ...photo,
            watermarkStatus
          }

          if (extractionStatus === WatermarkApplyStatus.EDITED) {
            successPhotos.push(updatedPhoto)

            // 如果提取成功且输出中包含水印信息，则更新水印数据到图片
            if (Array.isArray(item.items)) {
              // 为每张照片单独存储 watermarkID
              const watermarkID = item.watermark_id || ''
              watermarkData.watermarkIDMap.set(srcUrl, watermarkID)
              watermarkData.watermarkDataMap.set(srcUrl, item.items || [])
            }
          } else if (extractionStatus === 3) {
            // 提取超时
            const timeoutPhoto = {
              ...updatedPhoto,
              errorMsg: item.show_reason || item.reason || '水印提取超时',
              reason: item.reason || undefined,
              show_reason: item.show_reason || undefined
            }
            timeoutPhotos.push(timeoutPhoto)
            console.log('提取超时照片信息:', {
              src: srcUrl,
              show_reason: item.show_reason,
              reason: item.reason,
              taskID: timeoutPhoto.taskID,
              hasTaskID: !!timeoutPhoto.taskID,
              fullPhoto: timeoutPhoto
            })
          } else {
            const failedPhoto = {
              ...updatedPhoto,
              errorMsg: item.show_reason || item.reason || '提取失败',
              reason: item.reason || undefined // 保存原始 reason 字段（用于测试环境），如果为空则设为 undefined
            }
            failedPhotos.push(failedPhoto)
          }
        } else {
          console.error(`返回的结果未找到匹配照片的src字段：${srcUrl}`)
        }
      })

      return {
        successPhotos,
        failedPhotos,
        timeoutPhotos,
        watermarkData
      }
    },
    []
  )

  /**
   * 执行水印提取
   * @param pendingPhotos 待提取的照片列表
   * @param onPhotosStatusUpdate 照片状态更新回调
   * @param onWatermarkDataUpdate 水印数据更新回调
   * @param onExtractionComplete 提取完成回调，传递成功提取的照片列表
   * @param onExtractionStart 提取开始回调，传递照片数量
   * @param onExtractionEnd 提取结束回调
   */
  const extractWatermarks = useCallback(
    async (
      pendingPhotos: PhotoItem[],
      onPhotosStatusUpdate: (
        photos: PhotoItem[],
        status: WatermarkExtractionStatus
      ) => void,
      onWatermarkDataUpdate: (
        photoUrl: string,
        watermarkID: string,
        watermarkData: WatermarkItem[]
      ) => void,
      onExtractionComplete?: (successPhotos: PhotoItem[]) => void,
      onExtractionStart?: (photoCount: number) => void,
      onExtractionEnd?: () => void
    ): Promise<void> => {
      if (pendingPhotos.length === 0) {
        return
      }

      // 通知提取开始
      if (onExtractionStart) {
        onExtractionStart(pendingPhotos.length)
      }

      try {
        // 创建AI任务
        const taskResult = await createAITask({
          aiTaskType: 15,
          input: {
            urls: pendingPhotos.map((photo) => photo.smallURL),
            edit_user: userId || ''
          },
          userID: userId || '' // 使用从Redux状态获取的用户ID
        })

        const taskID = taskResult?.data?.taskID || ''

        if (!taskID) {
          console.warn('警告：createAITask 返回的 taskID 为空', {
            taskResult,
            data: taskResult?.data
          })
        }

        // 将 taskID 添加到所有待提取的照片中
        const photosWithTaskID = pendingPhotos.map((photo) => ({
          ...photo,
          taskID
        }))

        // 轮询任务结果，根据照片数量动态计算超时时间
        const result = await pollAITaskResult(
          {
            taskID: taskID,
            userID: userId || '' // 使用从Redux状态获取的用户ID
          },
          { photoCount: pendingPhotos.length }
        )

        // 检查任务状态
        const taskStatus = result.data?.taskStatus

        // 如果任务状态为4（处理失败），将所有pending照片标记为失败
        if (taskStatus === 4) {
          // 通知提取结束
          if (onExtractionEnd) {
            onExtractionEnd()
          }
          // 使用返回的错误消息，如果没有则使用默认消息
          const errorMsg =
            result.data?.msg && result.data?.msg.length > 0
              ? result.data?.msg
              : '水印任务处理失败，请稍后重试'
          message.error(errorMsg)
          // 使用带 taskID 的照片列表
          onPhotosStatusUpdate(
            photosWithTaskID,
            WatermarkExtractionStatus.FAILED
          )
          return
        }

        // 如果任务状态为5（超时完成），将所有pending照片标记为超时
        if (taskStatus === 5) {
          console.log('useWatermarkExtraction: 任务超时完成', {
            taskStatus,
            taskID,
            photosCount: photosWithTaskID.length,
            result
          })
          // 通知提取结束
          if (onExtractionEnd) {
            onExtractionEnd()
          }
          // 使用返回的错误消息，如果没有则使用默认消息
          const errorMsg =
            result.data?.msg && result.data?.msg.length > 0
              ? result.data?.msg
              : '水印提取超时，请稍后重试'
          message.warning(errorMsg)
          // 使用带 taskID 的照片列表，标记为超时状态
          const timeoutPhotos = photosWithTaskID.map((photo) => ({
            ...photo,
            watermarkStatus: WatermarkExtractionStatus.EXTRACTING_TIMEOUT,
            errorMsg: errorMsg,
            reason: errorMsg
          }))
          console.log('useWatermarkExtraction: 更新超时照片状态', {
            timeoutPhotosCount: timeoutPhotos.length,
            firstPhoto: timeoutPhotos[0]
          })
          onPhotosStatusUpdate(
            timeoutPhotos,
            WatermarkExtractionStatus.EXTRACTING_TIMEOUT
          )
          return
        }

        // 根据结果更新照片状态
        if (result.data?.output && result.data?.taskStatus === 3) {
          try {
            // 判断output是URL还是JSON字符串
            const isUrl =
              typeof result.data.output === 'string' &&
              (result.data.output.startsWith('http://') ||
                result.data.output.startsWith('https://'))

            let outputData: WatermarkApplyOutputResult[]

            if (isUrl) {
              // 如果是URL，从CDN获取内容
              outputData = await fetchOutputFromUrl<
                WatermarkApplyOutputResult[]
              >(result.data.output)
            } else {
              // 如果是JSON字符串，直接解析
              outputData = JSON.parse(result.data.output)
            }

            // 假设输出包含照片URL和对应的状态
            if (outputData && Array.isArray(outputData)) {
              // 处理提取结果（使用带 taskID 的照片列表）
              const {
                successPhotos,
                failedPhotos,
                timeoutPhotos,
                watermarkData
              } = processExtractionResults(outputData, photosWithTaskID)

              // 批量更新成功和失败的照片状态到PhotoPicker
              if (successPhotos.length > 0) {
                onPhotosStatusUpdate(
                  successPhotos,
                  WatermarkExtractionStatus.COMPLETED
                )

                // 更新水印数据，每张照片使用自己的 watermarkID
                watermarkData.watermarkDataMap.forEach((item, photoUrl) => {
                  // 从 watermarkIDMap 中获取该照片对应的 watermarkID
                  const watermarkID =
                    watermarkData.watermarkIDMap.get(photoUrl) || ''

                  onWatermarkDataUpdate(photoUrl, watermarkID, item)
                })

                // 通知提取完成，传递成功提取的照片列表
                if (onExtractionComplete) {
                  // 使用 setTimeout 确保所有状态更新完成后再回调
                  setTimeout(() => {
                    onExtractionComplete(successPhotos)
                  }, 100)
                }
              }

              if (timeoutPhotos.length > 0) {
                onPhotosStatusUpdate(
                  timeoutPhotos,
                  WatermarkExtractionStatus.EXTRACTING_TIMEOUT
                )
              }

              if (failedPhotos.length > 0) {
                onPhotosStatusUpdate(
                  failedPhotos,
                  WatermarkExtractionStatus.FAILED
                )
              }

              // 检查是否有照片被成功处理
              const totalProcessed =
                successPhotos.length +
                failedPhotos.length +
                timeoutPhotos.length

              if (totalProcessed === 0) {
                // 如果所有照片都没有匹配成功，说明 URL 匹配失败
                console.error(
                  'processExtractionResults: 所有照片都未匹配成功',
                  {
                    outputDataCount: outputData.length,
                    pendingPhotosCount: photosWithTaskID.length,
                    outputData: outputData.map((item) => ({
                      src: item.src,
                      normalizedSrc: normalizeUrl(item.src || ''),
                      success: item.success
                    })),
                    pendingPhotos: photosWithTaskID.map((p) => ({
                      smallURL: p.smallURL,
                      normalizedSmallURL: normalizeUrl(p.smallURL || ''),
                      taskID: p.taskID
                    }))
                  }
                )
                // 通知用户 URL 匹配失败
                message.error('提取结果与照片不匹配，请稍后重试')
                // 使用带 taskID 的照片列表，标记为超时而不是失败（因为接口返回成功，只是匹配失败）
                const timeoutPhotosWithError = photosWithTaskID.map(
                  (photo) => ({
                    ...photo,
                    watermarkStatus:
                      WatermarkExtractionStatus.EXTRACTING_TIMEOUT,
                    errorMsg: '提取结果与照片不匹配',
                    reason: 'URL匹配失败'
                  })
                )
                onPhotosStatusUpdate(
                  timeoutPhotosWithError,
                  WatermarkExtractionStatus.EXTRACTING_TIMEOUT
                )
              }

              // 通知提取结束（成功处理完成）
              if (onExtractionEnd) {
                onExtractionEnd()
              }
            } else {
              // 通知提取结束
              if (onExtractionEnd) {
                onExtractionEnd()
              }
              // 通知用户部分照片提取失败
              message.warning('提取结束，但数据异常导致提取失败')
              // 使用带 taskID 的照片列表
              onPhotosStatusUpdate(
                photosWithTaskID,
                WatermarkExtractionStatus.FAILED
              )
            }
          } catch (error) {
            // 通知提取结束
            if (onExtractionEnd) {
              onExtractionEnd()
            }
            console.error(
              '解析output数据失败（output格式错误，按超时处理）:',
              error
            )
            // output格式错误时，按照超时处理，而不是失败
            // 使用带 taskID 的照片列表
            const timeoutPhotosWithError = photosWithTaskID.map((photo) => ({
              ...photo,
              watermarkStatus: WatermarkExtractionStatus.EXTRACTING_TIMEOUT,
              errorMsg: '水印提取超时',
              reason: 'output格式错误'
            }))
            onPhotosStatusUpdate(
              timeoutPhotosWithError,
              WatermarkExtractionStatus.EXTRACTING_TIMEOUT
            )
          }
        } else if (taskStatus === 3) {
          // 通知提取结束
          if (onExtractionEnd) {
            onExtractionEnd()
          }
          // 如果taskStatus为3但没有output，将所有pending照片标记为失败
          message.error('水印提取完成但无结果，请稍后重试')
          // 使用带 taskID 的照片列表
          onPhotosStatusUpdate(
            photosWithTaskID,
            WatermarkExtractionStatus.FAILED
          )
        } else {
          // 通知提取结束（其他情况：1-队列中，2-处理中等）
          if (onExtractionEnd) {
            onExtractionEnd()
          }
          // 对于未完成的状态，也标记为超时，避免页面一直显示"提取中"
          console.warn('useWatermarkExtraction: 任务状态未完成', {
            taskStatus,
            taskID
          })
          const timeoutPhotos = photosWithTaskID.map((photo) => ({
            ...photo,
            watermarkStatus: WatermarkExtractionStatus.EXTRACTING_TIMEOUT,
            errorMsg: '水印提取超时',
            reason: `任务状态异常: ${taskStatus}`
          }))
          onPhotosStatusUpdate(
            timeoutPhotos,
            WatermarkExtractionStatus.EXTRACTING_TIMEOUT
          )
        }
      } catch (error) {
        // 通知提取结束
        if (onExtractionEnd) {
          onExtractionEnd()
        }
        console.error('Failed to create or poll AI task:', error)
        message.error('水印提取任务失败，请稍后重试')
        // 如果任务创建或轮询失败，将所有待提取照片标记为失败
        // 注意：如果任务创建失败，可能没有 taskID，所以使用原始照片列表
        onPhotosStatusUpdate(pendingPhotos, WatermarkExtractionStatus.FAILED)
      }
    },
    [createAITask, pollAITaskResult, userId, processExtractionResults]
  )

  return {
    extractWatermarks
  }
}
